import React, { useState } from 'react'
import parse from 'html-react-parser'
import { Grid, Paper, Typography, Button, Box } from '@material-ui/core'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined'
import AceEditor from 'react-ace'
import 'ace-builds'
import 'ace-builds/src-noconflict/theme-dracula'
import 'ace-builds/src-noconflict/ext-language_tools'
import 'ace-builds/src-noconflict/mode-c_cpp'
import 'ace-builds/src-noconflict/mode-java'
import 'ace-builds/src-noconflict/mode-python'
import 'ace-builds/src-noconflict/mode-golang'
import { aceModesForLanguages } from '../../utils'
import Loader from '../Loader/Loader'
import EditTestCase from '../EditTestCase/EditTestCase'
import { useStyles } from './AddCodingQuestionStyles'

const CodingQuestionTile = ({
  question,
  questionIndex,
  editQuestion,
  addQuestion,
  isPublished,
  isQuizStarted,
  quizStartTime,
  deleteQuestion,
  handleDelete,
  isWriteAllowed,
  handleQuestionEdit,
  calculateCodingMarks,
}) => {
  const classes = useStyles()
  const [currentSelectedLanguage, setCurrentSelectedLanguage] = useState(
    question?.stubs?.[0]?.language
  )

  const quizStarted = isQuizStarted(quizStartTime)
  const isEditing = editQuestion === questionIndex || addQuestion
  const isQuizActive = quizStarted && isPublished
  const showMarks = Number.isNaN(editQuestion) && !addQuestion
  const canShowDelete = !quizStarted || !isPublished
  const isDeleting = deleteQuestion?.isLoading

  return (
    <Grid container item xs={12} spacing={2}>
      <Grid item xs={10} sm={11}>
        <Paper className={classes.tileViewPaper} variant="outlined">
          <Grid container item xs={12} className={classes.questionHeader}>
            <Grid item xs={7} sm={4}>
              <Typography className={classes.questionIndex}>
                Question {questionIndex === undefined ? 1 : questionIndex + 1}.
              </Typography>
            </Grid>
            <Grid
              item
              xs={5}
              sm={8}
              container
              alignItems="center"
              justifyContent="flex-end"
            >
              {showMarks && (
                <Typography
                  variant="h4"
                  style={{ color: '#00B7A4' }}
                  className={classes.tileMarks}
                >
                  {calculateCodingMarks()} marks
                </Typography>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} className={classes.editQuestion}>
            <div className={classes.question}>{parse(question?.question)}</div>
          </Grid>

          <Grid item xs={12} className={classes.codeSnippetContainer}>
            <div className={classes.paper}>
              <Grid container>
                <Grid item xs={12}>
                  <Typography className={classes.codeSnippetTitle}>
                    Code Snippet:
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid className={classes.codeSnippetLanguagesMenu}>
                    {question?.stubs?.map((stub) => (
                      <Grid
                        key={stub?.language}
                        className={classes.languageBox}
                        onClick={() =>
                          setCurrentSelectedLanguage(stub?.language)
                        }
                        style={{
                          cursor: 'pointer',
                          borderBottom:
                            currentSelectedLanguage?.toLowerCase() ===
                            stub?.language
                              ? '4px solid #00B7A4'
                              : undefined,
                        }}
                      >
                        <Typography className={classes.selectedLanguageDisplay}>
                          {stub?.language}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid item xs={12} className={classes.codeStubWrapper}>
                  {question?.stubs?.map((codeStub) => (
                    <Grid container key={codeStub?.language}>
                      {codeStub?.language?.toLowerCase() ===
                        currentSelectedLanguage && (
                        <Grid
                          item
                          xs={12}
                          className={classes.codeStubLanguageBody}
                        >
                          <Typography>Body:</Typography>
                          <AceEditor
                            readOnly
                            mode={aceModesForLanguages(codeStub.language)}
                            theme="dracula"
                            defaultValue={codeStub?.functionSignature}
                            highlightActiveLine
                            enableBasicAutocompletion
                            width="100%"
                            height="150px"
                          />
                        </Grid>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </div>
          </Grid>

          {question?.testcases?.map((testcase, index) => (
            <Grid
              item
              xs={12}
              key={testcase?.id || index}
              className={classes.codeSnippetContainer}
            >
              <EditTestCase
                testcase={testcase}
                index={index}
                isQuizStarted={isQuizActive}
                editQuestion={editQuestion}
              />
            </Grid>
          ))}
        </Paper>
      </Grid>

      {isWriteAllowed && !isEditing && (
        <Grid item xs={2} sm={1}>
          <Button
            className={classes.editButton}
            variant="outlined"
            onClick={handleQuestionEdit}
            disabled={quizStarted}
          >
            <EditOutlinedIcon />
          </Button>

          {canShowDelete &&
            (isDeleting ? (
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingTop: '12px',
                }}
              >
                <Loader size={30} />
              </Box>
            ) : (
              <Button
                disabled={quizStarted}
                className={classes.deleteButton}
                variant="outlined"
                onClick={handleDelete}
              >
                <DeleteOutlineOutlinedIcon />
              </Button>
            ))}
        </Grid>
      )}
    </Grid>
  )
}

export default CodingQuestionTile
